// src/pages/About/OurService.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import Layout from '../../components/common/Layout/Layout';
// Import your logo image
import logoImage from '../../assets/images/winnestlogo.png'; // Adjust the path based on your asset structure

const OurService = () => {
    const [selectedService, setSelectedService] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Extract service name from URL
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const serviceName = urlParams.get('service');
        
        if (serviceName) {
            const service = services.find(s => 
                s.title.toLowerCase().replace(/\s+/g, '-') === serviceName.toLowerCase()
            );
            if (service) {
                setSelectedService(service);
            }
        } else {
            setSelectedService(null);
        }
    }, [location.search]);

    // Handle browser back/forward buttons
    useEffect(() => {
        const handlePopState = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const serviceName = urlParams.get('service');
            
            if (serviceName) {
                const service = services.find(s => 
                    s.title.toLowerCase().replace(/\s+/g, '-') === serviceName.toLowerCase()
                );
                setSelectedService(service || null);
            } else {
                setSelectedService(null);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const services = [
        {
            icon: 'piggy-bank',
            title: 'Financial Planning',
            description: 'Managing finances to ensure your success and satisfaction.',
            backgroundImage: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
            detailedContent: {
                subtitle: 'We bring you and your goals closer!',
                mainTitle: 'FINANCIAL PLANNING',
                overview: 'At WinNest, we evaluate the market in relation to your financial needs and, as a result, we tailor an investing plan for you. An ideal investment objective should be measurable and reachable. WinNest develops clear, acceptable, and practical goals that are simple to understand and implement.',
                sections: [
                    {
                        title: 'Why Choose WinNest for Financial Planning',
                        content: 'WinNest helps you reach your financial objectives, whether they be everyday goals like paying your bills or long-term goals like a financially secure retirement. We provide expert planners to help you design a financial strategy that is tailored to your specific needs.',
                        points: [
                            'We assist you in estimating the amount you will require to meet your objectives, taking inflation into account.',
                            'Expert planners design financial strategy tailored to your specific needs.',
                            'Clear, acceptable, and practical goals that are simple to understand and implement.',
                            'A broad investment strategy with numerous possibilities designed to fulfil your financial goals step by step.'
                        ]
                    },
                    {
                        title: 'Our Approach to Financial Planning',
                        content: 'It\'s very simple - one size does not fit. At WinNest, we evaluate the market in relation to your financial needs and tailor an investing plan for you.',
                        points: [
                            'Personalized financial strategies based on your specific goals',
                            'Market evaluation in relation to your financial needs',
                            'Measurable and reachable investment objectives',
                            'Step-by-step approach to achieve your financial goals'
                        ]
                    }
                ],
                keyFeatures: [
                    'BUILD LONG-TERM VALUE',
                    'PERSONALIZED PLANNING',
                    'EXPERT GUIDANCE',
                    'GOAL-ORIENTED APPROACH'
                ]
            }
        },
        {
            icon: 'umbrella',
            title: 'Retirement Planning',
            description: 'Prepare for a steady stream of money and retire like a hero.',
            backgroundImage: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
            detailedContent: {
                subtitle: 'Secure your future today!',
                mainTitle: 'RETIREMENT PLANNING',
                overview: 'At WinNest, we help you prepare for a steady stream of money and retire like a hero. Our retirement planning ensures you maintain your lifestyle and achieve financial independence in your golden years.',
                sections: [
                    {
                        title: 'Why Retirement Planning is Essential',
                        content: 'Retirement planning is crucial for maintaining your lifestyle after you stop working. At WinNest, we create strategies that ensure financial security during retirement.',
                        points: [
                            'Build a corpus that generates steady income post-retirement',
                            'Plan for inflation to maintain purchasing power',
                            'Create multiple income streams for financial security',
                            'Achieve financial independence to retire comfortably'
                        ]
                    },
                    {
                        title: 'Our Retirement Planning Strategy',
                        content: 'We design retirement plans that are tailored to your current financial situation and future aspirations.',
                        points: [
                            'Assess your current financial position and retirement needs',
                            'Calculate the required retirement corpus considering inflation',
                            'Diversify investments across various asset classes',
                            'Regular monitoring and adjustments to stay on track'
                        ]
                    }
                ],
                keyFeatures: [
                    'RETIREMENT CALCULATOR',
                    'PENSION PLANNING',
                    'VALUE PRESERVATION',
                    'EXPERT CONSULTATION'
                ]
            }
        },
        {
            icon: 'money-hand',
            title: 'Tax Planning',
            description: 'Analyzing and making plans to help you with your taxes.',
            backgroundImage: 'https://images.unsplash.com/photo-1554224154-22dec7ec8818?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
            detailedContent: {
                subtitle: 'Optimize your taxes legally!',
                mainTitle: 'TAX PLANNING',
                overview: 'Taxpayers may reduce their tax liabilities in many ways. At WinNest, we help you navigate through various tax deductions and exemptions available in India to optimize your tax burden legally.',
                sections: [
                    {
                        title: 'Tax Saving Strategies',
                        content: 'Section 80C is the most well-known of the several tax deductions and exemptions available in India. We help you leverage all available options.',
                        points: [
                            'PPF, 5-year bank deposits, NSCs, and ELSS investments',
                            'Strategic planning to maximize tax deductions',
                            'Utilize various sections of Income Tax Act',
                            'Legal tax optimization without compromising returns'
                        ]
                    },
                    {
                        title: 'Our Tax Planning Approach',
                        content: 'We create comprehensive tax planning strategies that align with your investment goals while maximizing tax benefits.',
                        points: [
                            'Analysis of your current tax situation',
                            'Identification of applicable tax-saving instruments',
                            'Integration of tax planning with financial goals',
                            'Regular review and optimization of tax strategies'
                        ]
                    }
                ],
                keyFeatures: [
                    'TAX OPTIMIZATION',
                    'ELSS INVESTMENTS',
                    'SECTION 80C BENEFITS',
                    'LEGAL COMPLIANCE'
                ]
            }
        },
        {
            icon: 'scale',
            title: 'Real Estate',
            description: 'Property with all-natural resources',
            backgroundImage: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
            detailedContent: {
                subtitle: 'Build your property portfolio!',
                mainTitle: 'REAL ESTATE INVESTMENT',
                overview: 'Real estate investment involves purchasing, owning, managing, renting, or selling real estate for profit. It\'s a popular investment strategy that can provide regular income and long-term appreciation.',
                sections: [
                    {
                        title: 'Why Invest in Real Estate',
                        content: 'Real estate has historically been a stable investment that provides both income and capital appreciation over time.',
                        points: [
                            'Hedge against inflation with property appreciation',
                            'Generate regular rental income',
                            'Build long-term value through property appreciation',
                            'Diversify your investment portfolio'
                        ]
                    },
                    {
                        title: 'Real Estate Investment Options',
                        content: '',
                        points: [
                            'Residential properties for rental income',
                            'Commercial real estate investments',
                            'REITs (Real Estate Investment Trusts)',
                            'Land banking for future development'
                        ]
                    }
                ],
                keyFeatures: [
                    'PROPERTY ANALYSIS',
                    'FINANCIAL PLANNING',
                    'MARKET RESEARCH',
                    'LEGAL SUPPORT'
                ]
            }
        },
        {
            icon: 'piggy-bank',
            title: 'Life Insurance',
            description: 'Life is unpredictable; give your family the best gift.',
            backgroundImage: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
            detailedContent: {
                subtitle: 'Protect your loved ones!',
                mainTitle: 'LIFE INSURANCE',
                overview: 'Life insurance provides financial protection for your family in case of your untimely demise. It ensures that your dependents can maintain their lifestyle and achieve their goals even in your absence.',
                sections: [
                    {
                        title: 'Importance of Life Insurance',
                        content: 'Life insurance is not just about death benefit; it\'s about ensuring your family\'s financial security and peace of mind.',
                        points: [
                            'Financial security for your dependents',
                            'Debt coverage and loan protection',
                            'Children\'s education fund security',
                            'Income replacement for your family'
                        ]
                    },
                    {
                        title: 'Types of Life Insurance',
                        content: '',
                        points: [
                            'Term Life Insurance for maximum coverage',
                            'Whole Life Insurance with savings component',
                            'ULIPs for investment and insurance',
                            'Endowment plans for long-term savings'
                        ]
                    }
                ],
                keyFeatures: [
                    'COVERAGE CALCULATOR',
                    'POLICY COMPARISON',
                    'CLAIM ASSISTANCE',
                    'HEALTH CHECK-UP'
                ]
            }
        },
        {
            icon: 'piggy-bank',
            title: 'Health Insurance',
            description: 'Cover your pre and post hospital bills with our assistance.',
            backgroundImage: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
            detailedContent: {
                subtitle: 'Your health, our priority!',
                mainTitle: 'HEALTH INSURANCE',
                overview: 'Health insurance provides coverage for medical expenses, ensuring that you and your family receive quality healthcare without financial stress. It protects your savings from unexpected medical emergencies.',
                sections: [
                    {
                        title: 'Why Health Insurance is Crucial',
                        content: 'With rising healthcare costs, health insurance has become essential for every family to avoid financial burden during medical emergencies.',
                        points: [
                            'Protection against rising medical costs',
                            'Cashless treatment at network hospitals',
                            'Coverage for pre and post hospitalization',
                            'Tax benefits under Section 80D'
                        ]
                    },
                    {
                        title: 'Health Insurance Features',
                        content: '',
                        points: [
                            'Comprehensive coverage for family',
                            'Critical illness coverage options',
                            'Preventive health check-ups',
                            'No-claim bonus for claim-free years'
                        ]
                    }
                ],
                keyFeatures: [
                    'FAMILY FLOATER PLANS',
                    'CRITICAL ILLNESS COVER',
                    'CASHLESS TREATMENT',
                    'WELLNESS PROGRAMS'
                ]
            }
        },
        {
            icon: 'umbrella',
            title: 'Mutual Funds',
            description: 'Invest systematically for future returns and attain your goals.',
            backgroundImage: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
            detailedContent: {
                subtitle: 'Invest systematically and achieve your goals!',
                mainTitle: 'MUTUAL FUNDS',
                overview: 'At WinNest, we make the investment process easy for you. Our plans are carefully designed with Science, data and technology and, most notably, our knowledge, experience and personal touch to make your win. We all can win with WinNest.',
                sections: [
                    {
                        title: 'Why Choose Mutual Funds with WinNest',
                        content: 'Mutual funds offer a systematic way to invest and achieve your financial goals. Our expert guidance ensures you make informed investment decisions.',
                        points: [
                            'Professional fund management with expert research',
                            'Diversification across various sectors and companies',
                            'Systematic Investment Plans (SIP) for disciplined investing',
                            'Goal-based investment approach for better outcomes'
                        ]
                    },
                    {
                        title: 'Our Mutual Fund Strategy',
                        content: 'We help you select the right mutual funds based on your risk profile, investment horizon, and financial goals.',
                        points: [
                            'Equity funds for long-term value creation',
                            'Debt funds for stable returns and capital protection',
                            'Hybrid funds for balanced risk and return',
                            'ELSS funds for tax saving under Section 80C'
                        ]
                    }
                ],
                keyFeatures: [
                    'SIP PLANNING',
                    'GOAL-BASED INVESTING',
                    'EXPERT SELECTION',
                    'REGULAR MONITORING'
                ]
            }
        },
        {
            icon: 'piggy-bank',
            title: 'Stocks',
            description: 'Inflation-protection. Liquidity. Advantage of the Booming Generation.',
            backgroundImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
            detailedContent: {
                subtitle: 'Take advantage of the booming generation!',
                mainTitle: 'STOCK INVESTMENTS',
                overview: 'Stocks offer inflation-protection, liquidity, and the advantage of the booming generation. At WinNest, we help you navigate the stock market with expert guidance and research-backed recommendations.',
                sections: [
                    {
                        title: 'Benefits of Stock Investment',
                        content: 'Stocks have historically provided competitive returns over the long term, though past performance does not guarantee future results.',
                        points: [
                            'Beat inflation with potential for high returns',
                            'Liquidity - buy and sell easily during market hours',
                            'Ownership in growing companies and economies',
                            'Dividend income from profitable companies'
                        ]
                    },
                    {
                        title: 'Our Stock Investment Approach',
                        content: 'We provide research-backed stock recommendations and help you build a diversified portfolio aligned with your risk profile.',
                        points: [
                            'Fundamental analysis for stock selection',
                            'Diversification across sectors and market caps',
                            'Long-term investment approach for value creation',
                            'Regular portfolio review and rebalancing'
                        ]
                    }
                ],
                keyFeatures: [
                    'RESEARCH REPORTS',
                    'PORTFOLIO GUIDANCE',
                    'MARKET INSIGHTS',
                    'EXPERT RECOMMENDATIONS'
                ]
            }
        },
        {
            icon: 'scroll',
            title: 'Legacy Planning',
            description: 'Secure your family\'s future with comprehensive legacy and estate planning.',
            backgroundImage: 'https://images.unsplash.com/photo-1554224154-1696413565d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
            detailedContent: {
                subtitle: 'Preserve your assets for generations!',
                mainTitle: 'LEGACY PLANNING',
                overview: 'Legacy planning is about ensuring your assets and values are transferred to future generations efficiently and according to your wishes. At WinNest, we help you create a comprehensive legacy plan that protects your assets, minimizes taxes, and provides for your loved ones.',
                sections: [
                    {
                        title: 'Why Legacy Planning is Essential',
                        content: 'Legacy planning ensures your hard-earned assets are preserved and transferred according to your wishes, while minimizing tax implications and family disputes.',
                        points: [
                            'Protect your assets from unnecessary taxes and legal complications',
                            'Ensure smooth transfer of assets to beneficiaries',
                            'Minimize estate taxes and maximize inheritance value',
                            'Prevent family disputes and legal challenges'
                        ]
                    },
                    {
                        title: 'Our Legacy Planning Services',
                        content: 'We provide comprehensive legacy planning services that cover all aspects of asset transfer and estate management.',
                        points: [
                            'Estate planning and asset structuring strategies',
                            'Trust formation and management guidance',
                            'Tax-efficient asset transfer mechanisms',
                            'Succession planning for business owners'
                        ]
                    }
                ],
                keyFeatures: [
                    'ESTATE PLANNING',
                    'TRUST SERVICES',
                    'TAX OPTIMIZATION',
                    'SUCCESSION PLANNING'
                ]
            }
        },
        {
            icon: 'document',
            title: 'Will Writing',
            description: 'Ensure your wishes are honored with professionally drafted wills and legal documentation.',
            backgroundImage: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
            detailedContent: {
                subtitle: 'Your wishes, legally protected!',
                mainTitle: 'WILL WRITING SERVICES',
                overview: 'A will is one of the most important legal documents you can have. It ensures your assets are distributed according to your wishes and provides peace of mind for you and your family. At WinNest, we help you create legally sound wills that protect your interests.',
                sections: [
                    {
                        title: 'Importance of Having a Will',
                        content: 'A properly drafted will ensures your assets are distributed according to your wishes and helps avoid family disputes and legal complications.',
                        points: [
                            'Ensure your assets go to intended beneficiaries',
                            'Appoint guardians for minor children',
                            'Minimize probate costs and legal complications',
                            'Provide clear instructions for asset distribution'
                        ]
                    },
                    {
                        title: 'Our Will Writing Process',
                        content: 'We follow a systematic approach to ensure your will is comprehensive, legally valid, and reflects your true intentions.',
                        points: [
                            'Detailed consultation to understand your wishes',
                            'Professional drafting by legal experts',
                            'Regular review and updates as needed',
                            'Secure storage and execution guidance'
                        ]
                    }
                ],
                keyFeatures: [
                    'LEGAL COMPLIANCE',
                    'EXPERT DRAFTING',
                    'SECURE STORAGE',
                    'REGULAR UPDATES'
                ]
            }
        }
    ];

    const handleServiceClick = (service) => {
        const serviceName = service.title.toLowerCase().replace(/\s+/g, '-');
        navigate(`?service=${serviceName}`, { replace: false });
        setSelectedService(service);
        // Scroll to top to show the detailed view
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleBackToServices = () => {
        navigate('/service', { replace: false });
        setSelectedService(null);
        // Scroll to the services section
        setTimeout(() => {
            window.scrollTo({ top: 400, behavior: 'smooth' });
        }, 100);
    };

    const styles = {
        pageContainer: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column'
        },
        heroSection: {
            backgroundColor: '#f5f5f5',
            padding: '4rem 0',
            position: 'relative',
            overflow: 'hidden',
            backgroundImage: selectedService 
                ? 'url("https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")'
                : 'url("https://images.unsplash.com/photo-1554224154-26032fced8bd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            minHeight: selectedService ? '500px' : '600px',
            filter: 'none'
        },
        heroOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: selectedService 
                ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.75) 50%, rgba(34, 197, 94, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 50%, rgba(34, 197, 94, 0.15) 100%)',
            zIndex: 1
        },
        heroContainer: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 2rem',
            position: 'relative',
            zIndex: 2
        },
        heroContent: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: selectedService ? 'center' : 'space-between',
            minHeight: '400px'
        },
        heroLeft: {
            flex: selectedService ? 'none' : 1,
            maxWidth: selectedService ? '800px' : '600px',
            backgroundColor: selectedService ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.85)',
            backdropFilter: selectedService ? 'blur(20px) saturate(180%)' : 'blur(15px) saturate(160%)',
            borderRadius: selectedService ? '25px' : '20px',
            padding: selectedService ? '4rem' : '3rem',
            border: selectedService ? '1px solid rgba(255, 255, 255, 0.6)' : '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: selectedService ? '0 25px 50px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)' : '0 20px 40px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
            textAlign: selectedService ? 'center' : 'left'
        },
        heroTitle: {
            fontSize: '4rem',
            fontWeight: '700',
            color: '#1a1a1a',
            margin: '0 0 2rem 0',
            lineHeight: '1.1',
            textShadow: '0 2px 10px rgba(255, 255, 255, 0.5)'
        },
        heroRight: {
            flex: 1,
            display: selectedService ? 'none' : 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
        },
        financialGraphic: {
            width: '450px',
            height: '350px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(15px) saturate(160%)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2rem',
            padding: '2rem'
        },
        graphicTitle: {
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#1a1a1a',
            textAlign: 'center',
            marginBottom: '1rem'
        },
        graphicSubtitle: {
            fontSize: '1rem',
            color: '#6b7280',
            textAlign: 'center',
            lineHeight: '1.5'
        },
        chartContainer: {
            display: 'flex',
            alignItems: 'flex-end',
            gap: '2rem',
            width: '100%',
            justifyContent: 'center',
            height: '150px'  
        },
        chartBar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
    height: '100%',                // Add this
    justifyContent: 'flex-end'     // Add this
},
        bar: {
            width: '40px',
            backgroundColor: '#22c55e',
            borderRadius: '4px 4px 0 0',
            position: 'relative'
        },
        barLabel: {
            fontSize: '0.8rem',
            color: '#6b7280',
            fontWeight: '500'
        },
        breadcrumb: {
            fontSize: '1rem',
            color: 'rgba(26, 26, 26, 0.8)',
            marginBottom: '1rem',
            backgroundColor: selectedService ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
            padding: selectedService ? '0.75rem 1.5rem' : '0',
            borderRadius: selectedService ? '12px' : '0',
            backdropFilter: selectedService ? 'blur(15px) saturate(180%)' : 'none',
            border: selectedService ? '1px solid rgba(255, 255, 255, 0.6)' : 'none',
            display: 'inline-block',
            boxShadow: selectedService ? '0 8px 25px rgba(0, 0, 0, 0.1)' : 'none'
        },
        backButton: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: '#22c55e',
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            marginBottom: '2rem',
            transition: 'background-color 0.3s ease'
        },
        mainSection: {
            backgroundColor: '#ffffff',
            padding: '4rem 0',
            flex: 1
        },
        container: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 2rem'
        },
        sectionHeader: {
            textAlign: 'center',
            marginBottom: '3rem'
        },
        sectionTitle: {
            fontSize: '3rem',
            fontWeight: '700',
            color: '#1a1a1a',
            margin: '0 0 1rem 0'
        },
        sectionSubtitle: {
            fontSize: '1.2rem',
            color: '#6b7280',
            margin: '0'
        },
        servicesGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            marginBottom: '4rem'
        },
        serviceCard: {
            position: 'relative',
            padding: '2rem',
            borderRadius: '20px',
            textAlign: 'left',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            overflow: 'hidden',
            minHeight: '280px',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
        },
        serviceIcon: {
            width: '48px',
            height: '48px',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#22c55e',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '12px',
            position: 'relative',
            zIndex: 3,
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
        },
        serviceTitle: {
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#1a1a1a',
            margin: '0 0 1rem 0',
            textShadow: '0 2px 10px rgba(255, 255, 255, 0.8)',
            position: 'relative',
            zIndex: 3
        },
        serviceDescription: {
            fontSize: '1rem',
            color: '#374151',
            lineHeight: '1.6',
            margin: '0',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '0.75rem',
            borderRadius: '8px',
            position: 'relative',
            zIndex: 3,
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
        },
        // Glass overlay for service cards
        serviceCardOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(34, 197, 94, 0.15) 100%)',
            borderRadius: '20px',
            zIndex: 1
        },
        // Detailed view styles
        detailsContainer: {
            display: 'flex',
            gap: '4rem',
            alignItems: 'flex-start'
        },
        detailsLeft: {
            flex: 2
        },
        detailsRight: {
            flex: 1,
            position: 'sticky',
            top: '2rem'
        },
        detailSubtitle: {
            fontSize: '1.1rem',
            color: 'rgba(26, 26, 26, 0.8)',
            marginBottom: '1rem',
            backgroundColor: selectedService ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
            padding: selectedService ? '1rem 2rem' : '0',
            borderRadius: selectedService ? '15px' : '0',
            backdropFilter: selectedService ? 'blur(15px) saturate(180%)' : 'none',
            border: selectedService ? '1px solid rgba(255, 255, 255, 0.6)' : 'none',
            display: 'inline-block',
            boxShadow: selectedService ? '0 8px 25px rgba(0, 0, 0, 0.1)' : 'none'
        },
        detailMainTitle: {
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#1a1a1a',
            marginBottom: '2rem'
        },
        detailOverview: {
            fontSize: '1.1rem',
            color: '#4b5563',
            lineHeight: '1.7',
            marginBottom: '3rem'
        },
        detailSection: {
            marginBottom: '3rem'
        },
        detailSectionTitle: {
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#1a1a1a',
            marginBottom: '1rem'
        },
        detailSectionContent: {
            fontSize: '1rem',
            color: '#4b5563',
            lineHeight: '1.6',
            marginBottom: '1rem'
        },
        detailPointsList: {
            marginLeft: '1.5rem'
        },
        detailPoint: {
            fontSize: '1rem',
            color: '#4b5563',
            lineHeight: '1.6',
            marginBottom: '0.5rem'
        },
        keyFeaturesCard: {
            backgroundColor: '#f9fafb',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
        },
        keyFeaturesTitle: {
            fontSize: '1.3rem',
            fontWeight: '600',
            color: '#1a1a1a',
            marginBottom: '1.5rem'
        },
        keyFeatureItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem',
            marginBottom: '0.5rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
        },
        keyFeatureIcon: {
            width: '24px',
            height: '24px',
            backgroundColor: '#22c55e',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '12px'
        },
        keyFeatureText: {
            fontSize: '0.9rem',
            fontWeight: '500',
            color: '#374151'
        },
        // Mobile app section styles
        mobileAppSection: {
            backgroundColor: '#f5f5f5',
            padding: '4rem 0'
        },
        mobileContent: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '4rem'
        },
        mobileLeft: {
            flex: 1,
            maxWidth: '600px'
        },
        mobileTitle: {
            fontSize: '3rem',
            fontWeight: '700',
            color: '#1a1a1a',
            margin: '0 0 1.5rem 0',
            lineHeight: '1.2'
        },
        mobileDescription: {
            fontSize: '1.1rem',
            color: '#6b7280',
            lineHeight: '1.6',
            margin: '0 0 2rem 0'
        },
        appButtons: {
            display: 'flex',
            gap: '1rem'
        },
        appButton: {
            display: 'block',
            borderRadius: '8px',
            overflow: 'hidden',
            transition: 'transform 0.3s ease'
        },
        appButtonImage: {
            height: '60px',
            width: 'auto',
            display: 'block'
        },
        mobileRight: {
            flex: 1,
            display: 'flex',
            justifyContent: 'center'
        },
        phoneContainer: {
            position: 'relative',
            width: '320px',
            height: '640px'
        },
        phoneFrame: {
            width: '100%',
            height: '100%',
            backgroundColor: '#1a1a1a',
            borderRadius: '35px',
            padding: '15px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
        },
        phoneScreen: {
            width: '100%',
            height: '100%',
            backgroundColor: '#ffffff',
            borderRadius: '25px',
            overflow: 'hidden',
            position: 'relative'
        },
        phoneHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px 10px',
            backgroundColor: '#ffffff'
        },
        phoneTime: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#000'
        },
        phoneIndicators: {
            display: 'flex',
            gap: '5px',
            alignItems: 'center'
        },
        phoneIndicator: {
            width: '18px',
            height: '12px',
            backgroundColor: '#000',
            borderRadius: '2px'
        },
        phoneStatusBar: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 20px',
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #f0f0f0'
        },
        phoneTitle: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        phoneLogo: {
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            objectFit: 'cover'
        },
        phoneAppName: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#000'
        },
        phoneMenu: {
            width: '24px',
            height: '24px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '3px'
        },
        phoneMenuLine: {
            width: '100%',
            height: '2px',
            backgroundColor: '#000',
            borderRadius: '1px'
        },
        phoneMainContent: {
            padding: '20px',
            backgroundColor: '#ffffff'
        },
        phoneWelcome: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#22c55e',
            marginBottom: '15px'
        },
        phoneImageContainer: {
            width: '100%',
            height: '200px',
            borderRadius: '15px',
            overflow: 'hidden',
            marginBottom: '20px',
            position: 'relative',
            backgroundImage: 'url("https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
        },
        phoneImageOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.8) 0%, rgba(22, 163, 74, 0.8) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        phoneImageIcon: {
            fontSize: '60px',
            color: 'white'
        },
        phoneChartsContainer: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '10px',
            marginTop: '15px'
        },
        phoneChart: {
            backgroundColor: '#f8f9fa',
            padding: '15px 8px',
            borderRadius: '12px',
            textAlign: 'center',
            minHeight: '80px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
        },
        phoneChartTitle: {
            fontSize: '8px',
            color: '#6b7280',
            marginBottom: '5px',
            fontWeight: '500'
        },
        phoneChartIcon: {
            fontSize: '24px',
            marginBottom: '5px'
        },
        phoneChartValue: {
            fontSize: '10px',
            color: '#374151',
            fontWeight: '600'
        }
    };

    const renderIcon = (iconType) => {
        switch(iconType) {
            case 'piggy-bank':
                return (
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M8 32c0-8.8 7.2-16 16-16s16 7.2 16 16v8H8v-8z" />
                        <circle cx="24" cy="24" r="2" />
                        <path d="M40 28h4v4h-4z" />
                        <path d="M16 40v4" />
                        <path d="M32 40v4" />
                    </svg>
                );
            case 'umbrella':
                return (
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M8 24c0-8.8 7.2-16 16-16s16 7.2 16 16" />
                        <path d="M24 8v36" />
                        <path d="M32 40c0 2.2-1.8 4-4 4s-4-1.8-4-4" />
                    </svg>
                );
            case 'money-hand':
                return (
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 32c0-4.4 3.6-8 8-8h8c4.4 0 8 3.6 8 8v4c0 2.2-1.8 4-4 4H16c-2.2 0-4-1.8-4-4v-4z" />
                        <circle cx="24" cy="20" r="4" />
                        <path d="M16 16l-4-4" />
                        <path d="M32 16l4-4" />
                    </svg>
                );
            case 'scale':
                return (
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 20l6-6 6 6" />
                        <path d="M30 20l6-6 6 6" />
                        <path d="M24 8v32" />
                        <path d="M8 40h32" />
                        <path d="M18 32h-12l6-12 6 12z" />
                        <path d="M42 32h-12l6-12 6 12z" />
                    </svg>
                );
            case 'scroll':
                return (
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M8 6h32v36H8z" />
                        <path d="M16 16h16" />
                        <path d="M16 24h16" />
                        <path d="M16 32h8" />
                    </svg>
                );
            case 'document':
                return (
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14 2v44l20-10 20 10V2H14z" />
                        <path d="M24 14h10" />
                        <path d="M24 20h10" />
                        <path d="M24 26h6" />
                    </svg>
                );
            default:
                return (
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="24" cy="24" r="20" />
                    </svg>
                );
        }
    };

    return (
        <>
            <style>{`
        .service-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
        }
        
        .service-card:hover .service-card-overlay {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0.25) 50%, rgba(34, 197, 94, 0.25) 100%);
        }
        
        .service-card:hover .service-title {
          color: #22c55e;
        }
        
        .app-button:hover {
          transform: scale(1.05);
        }
        
        .back-button:hover {
          background-color: #16a34a;
        }
        
        .key-feature-item:hover {
          background-color: #f3f4f6;
          border-color: #22c55e;
        }
        
        @media (max-width: 1024px) {
          .hero-content {
            flex-direction: column;
            text-align: center;
            gap: 2rem;
          }
          
          .mobile-content {
            flex-direction: column;
            text-align: center;
          }
          
          .details-container {
            flex-direction: column;
            gap: 2rem;
          }
          
          .hero-title {
            font-size: 3rem !important;
          }
          
          .section-title {
            font-size: 2.5rem !important;
          }
          
          .mobile-title {
            font-size: 2.5rem !important;
          }
          
          .detail-main-title {
            font-size: 2rem !important;
          }
        }
        
        @media (max-width: 768px) {
          .hero-container {
            padding: 0 1rem !important;
          }
          
          .container {
            padding: 0 1rem !important;
          }
          
          .hero-title {
            font-size: 2.5rem !important;
          }
          
          .section-title {
            font-size: 2rem !important;
          }
          
          .mobile-title {
            font-size: 2rem !important;
          }
          
          .detail-main-title {
            font-size: 1.8rem !important;
          }
          
          .services-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
          }
          
          .app-buttons {
            flex-direction: column;
            align-items: center;
          }
          
          .phone-container {
            width: 280px !important;
            height: 560px !important;
          }
        }
        
        @media (max-width: 640px) {
          .hero-title {
            font-size: 2rem !important;
          }
          
          .section-title {
            font-size: 1.8rem !important;
          }
          
          .mobile-title {
            font-size: 1.8rem !important;
          }
          
          .detail-main-title {
            font-size: 1.5rem !important;
          }
        }
      `}</style>

            <div style={styles.pageContainer}>
                {/* Header */}
                <Header />

                {/* Hero Section */}
                <section style={styles.heroSection}>
                    <div style={styles.heroOverlay}></div>
                    <div style={styles.heroContainer}>
                        <div style={styles.heroContent} className="hero-content">
                            <div style={styles.heroLeft}>
                                {selectedService && (
                                    <div style={styles.breadcrumb}>
                                        Home / {selectedService.title}
                                    </div>
                                )}
                                <h1 style={styles.heroTitle} className="hero-title">
                                    {selectedService ? selectedService.title : 'Our Services'}
                                </h1>
                                {selectedService && (
                                    <p style={styles.detailSubtitle}>
                                        {selectedService.detailedContent.subtitle}
                                    </p>
                                )}
                            </div>
                            <div style={styles.heroRight}>
                                <div style={styles.financialGraphic}>
                                    <div style={styles.graphicTitle}>Our Investment Approach</div>
                                    <div style={styles.graphicSubtitle}>Creating value through strategic investment solutions designed for long-term financial growth</div>
                                    <div style={styles.chartContainer}>
                                        <div style={styles.chartBar}>
                                            <div style={{...styles.bar, height: '60px'}}></div>
                                            <div style={styles.barLabel}>2023</div>
                                        </div>
                                        <div style={styles.chartBar}>
                                            <div style={{...styles.bar, height: '80px'}}></div>
                                            <div style={styles.barLabel}>2024</div>
                                        </div>
                                        <div style={styles.chartBar}>
                                            <div style={{...styles.bar, height: '100px'}}></div>
                                            <div style={styles.barLabel}>2025</div>
                                        </div>
                                        <div style={styles.chartBar}>
                                            <div style={{...styles.bar, height: '120px', backgroundColor: '#16a34a'}}></div>
                                            <div style={styles.barLabel}>Goal</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
{/* Main Content */}
                <main>
                    <section style={styles.mainSection}>
                        <div style={styles.container}>
                            {selectedService ? (
                                // Detailed Service View
                                <>
                                    <button 
                                        onClick={handleBackToServices}
                                        style={styles.backButton}
                                        className="back-button"
                                    >
                                        ← Back to Services
                                    </button>
                                    
                                    <div style={styles.detailsContainer} className="details-container">
                                        <div style={styles.detailsLeft}>
                                            <p style={styles.detailSubtitle}>
                                                {selectedService.detailedContent.subtitle}
                                            </p>
                                            <h2 style={styles.detailMainTitle} className="detail-main-title">
                                                {selectedService.detailedContent.mainTitle}
                                            </h2>
                                            <p style={styles.detailOverview}>
                                                {selectedService.detailedContent.overview}
                                            </p>

                                            {selectedService.detailedContent.sections.map((section, index) => (
                                                <div key={index} style={styles.detailSection}>
                                                    <h3 style={styles.detailSectionTitle}>
                                                        {section.title}
                                                    </h3>
                                                    {section.content && (
                                                        <p style={styles.detailSectionContent}>
                                                            {section.content}
                                                        </p>
                                                    )}
                                                    <ul style={styles.detailPointsList}>
                                                        {section.points.map((point, pointIndex) => (
                                                            <li key={pointIndex} style={styles.detailPoint}>
                                                                {point}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>

                                        <div style={styles.detailsRight}>
                                            <div style={styles.keyFeaturesCard}>
                                                <h3 style={styles.keyFeaturesTitle}>Key features</h3>
                                                {selectedService.detailedContent.keyFeatures.map((feature, index) => (
                                                    <div 
                                                        key={index} 
                                                        style={styles.keyFeatureItem}
                                                        className="key-feature-item"
                                                    >
                                                        <div style={styles.keyFeatureIcon}>
                                                            {index === 0 ? '₹' : index === 1 ? '♥' : index === 2 ? '💡' : '✉'}
                                                        </div>
                                                        <span style={styles.keyFeatureText}>
                                                            {feature}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                // Services Grid View
                                <>
                                    {/* Section Header */}
                                    <div style={styles.sectionHeader}>
                                        <h2 style={styles.sectionTitle} className="section-title">
                                            We bring you and your goals closer!
                                        </h2>
                                        <p style={styles.sectionSubtitle}>
                                            We all can win together.
                                        </p>
                                    </div>

                                    {/* Services Grid */}
                                    <div style={styles.servicesGrid} className="services-grid">
                                        {services.map((service, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    ...styles.serviceCard,
                                                    backgroundImage: `url(${service.backgroundImage})`
                                                }}
                                                className="service-card"
                                                onClick={() => handleServiceClick(service)}
                                            >
                                                {/* Glass overlay */}
                                                <div 
                                                    style={styles.serviceCardOverlay} 
                                                    className="service-card-overlay"
                                                ></div>
                                                
                                                {/* Card content */}
                                                <div>
                                                    <div style={styles.serviceIcon}>
                                                        {renderIcon(service.icon)}
                                                    </div>
                                                    <h3 style={styles.serviceTitle} className="service-title">
                                                        {service.title}
                                                    </h3>
                                                </div>
                                                
                                                <p style={styles.serviceDescription}>
                                                    {service.description}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </main>

                {/* Layout*/}
                <Layout />

                {/* Footer */}
                <Footer />
            </div>
        </>
    );
};

export default OurService;